import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useRequestContactsExportMutation } from "@/pages/Private/redux/contact/contact.api";

import { ContactSelectType } from "../schema/contacts";
import { buildExportPayload } from "../utils/buildExportPayload";
import { useExportDownloadLink } from "./useExportDownloadLink";

interface UseContactsExportParams {
	selectType: ContactSelectType;
	selectedItems: number[];
	filter?: Record<string, unknown>;
	query?: string;
	semanticSearchProspects?: string;
	/** The page's snackbar. Every outcome is announced through it — silence was the defect. */
	openSnackbar: (message: string, duration?: number) => void;
}

interface UseContactsExport {
	handleExport: () => Promise<void>;
	isExportLoading: boolean;
}

const QUEUED_SNACKBAR_MS = 6000;

/**
 * Both halves of a Contacts export (PREC-3953): requesting one, and downloading it when the
 * "your export is ready" notification brings the user back with `?export=<id>`. They live
 * together because they are the two ends of the same round trip.
 *
 * The file is built off-request, so requesting only hands over the selection and says so.
 */
export function useContactsExport({
	selectType,
	selectedItems,
	filter,
	query,
	semanticSearchProspects,
	openSnackbar,
}: UseContactsExportParams): UseContactsExport {
	const { t } = useTranslation();
	const [requestExport, { isLoading: isExportLoading }] = useRequestContactsExportMutation();

	const reportError = useCallback(
		(message?: string) => openSnackbar(message ?? t("basics.error")),
		[openSnackbar, t]
	);

	useExportDownloadLink({ onError: reportError });

	const handleExport = useCallback(async () => {
		try {
			const payload = buildExportPayload({
				selectType,
				selectedItems,
				filter,
				query,
				semanticSearchProspects,
			});

			await requestExport(payload).unwrap();
			openSnackbar(t("contacts.exportStarted"), QUEUED_SNACKBAR_MS);
		} catch (error) {
			console.error(error);
			reportError();
		}
	}, [
		requestExport,
		selectType,
		selectedItems,
		filter,
		query,
		semanticSearchProspects,
		openSnackbar,
		reportError,
		t,
	]);

	return { handleExport, isExportLoading };
}
